import { createContext, useContext, useMemo, type JSX, type ReactNode } from 'react';

import type { ProjectChatCreditChrome } from './ProjectChat';

/** The chat's credit chrome, read as a hook so the banner state stays with the caller. */
export type UseWorkCreditAllowanceChrome = (options: {
  inThread: boolean;
}) => ProjectChatCreditChrome;

const STUB_CHROME: ProjectChatCreditChrome = {
  composerDockLeading: null,
  transcriptTrailing: null,
  hideFollowUpQuestions: false,
  sendDisabled: false,
  forceEvidenceOnlyOnSend: false,
};

type WorkCreditAllowanceChromeByPlacement = {
  landing: ProjectChatCreditChrome;
  inThread: ProjectChatCreditChrome;
};

const STUB_BY_PLACEMENT: WorkCreditAllowanceChromeByPlacement = {
  landing: STUB_CHROME,
  inThread: STUB_CHROME,
};

const WorkCreditAllowanceChromeContext =
  createContext<WorkCreditAllowanceChromeByPlacement>(STUB_BY_PLACEMENT);

/**
 * Runs the chrome hook once per placement so SessionWorkPanel + ProjectChat
 * (and Work home + ProjectChat) share one subscription each instead of N.
 */
export const WorkCreditAllowanceChromeProvider = ({
  useChrome,
  children,
}: {
  useChrome: UseWorkCreditAllowanceChrome;
  children: ReactNode;
}): JSX.Element => {
  const landing = useChrome({ inThread: false });
  const inThread = useChrome({ inThread: true });
  const value = useMemo(() => ({ landing, inThread }), [inThread, landing]);
  return (
    <WorkCreditAllowanceChromeContext.Provider value={value}>
      {children}
    </WorkCreditAllowanceChromeContext.Provider>
  );
};

/** Package-internal. With no provider this returns the no-hook stub. */
export const useWorkCreditAllowanceChrome = (options: {
  inThread: boolean;
}): ProjectChatCreditChrome => {
  const byPlacement = useContext(WorkCreditAllowanceChromeContext);
  return options.inThread ? byPlacement.inThread : byPlacement.landing;
};
